import { useState } from "react";
import type { MetricSeries } from "../../hooks/useAdminStatus";
import {
  ChartTooltip,
  tooltipStyles as tip,
  type ChartTooltipAnchor,
} from "./chart/ChartTooltip";
import { bucketRangeLabel, metricValueLabel } from "../../lib/chartBuckets";
import styles from "./MetricsChart.module.css";

// 0-100% line + area chart over one MetricSeries' buckets, sharing the 480x120
// viewBox + AXIS_W left gutter with UsageChart/ToolCallsChart so the panels line
// up. Hovering a bucket surfaces the same portaled ChartTooltip the spend chart
// uses (not a native <title>), and a left percent axis labels the scale. null
// points (no datapoint in that bucket) break the line into segments rather than
// forward-filling, so a real gap reads as "no data here", not flat.
const WIDTH = 480;
const HEIGHT = 120;
const PAD_TOP = 6;
const PAD_BOTTOM = 6;
const AXIS_W = 34;

const METRIC_LABEL: Record<string, string> = { cpu: "CPU", mem: "Memory", swap: "Swap", disk: "Disk" };

type Pt = {
  readonly x: number;
  readonly y: number;
  readonly value: number;
  readonly t: string;
  readonly i: number;
};

type Hover = ChartTooltipAnchor & { readonly value: number; readonly t: string };

// Split into runs of consecutive non-null points; each run becomes one polyline
// (and the first run anchors the filled area, matching the single-line look).
const toSegments = (pts: ReadonlyArray<Pt | null>): ReadonlyArray<ReadonlyArray<Pt>> => {
  const segments: Array<Array<Pt>> = [];
  let current: Array<Pt> = [];
  for (const p of pts) {
    if (p === null) {
      if (current.length > 0) {
        segments.push(current);
        current = [];
      }
    } else {
      current.push(p);
    }
  }
  if (current.length > 0) segments.push(current);
  return segments;
};

export const MetricsChart = ({
  series,
  bucketSeconds,
  totalGib,
  threshold,
}: {
  readonly series: MetricSeries;
  readonly bucketSeconds: number;
  // Capacity (GiB) for this metric, when known: drives the absolute value in the
  // tooltip ("41.2% (1.6 / 4 GB)"). null/undefined -> percentage only.
  readonly totalGib?: number | null;
  // Percent above which this metric is dangerous, when it has such a level.
  // Most metrics don't: 60% disk or CPU is unremarkable. Swap is the exception
  // - healthy is ~0 and sustained use precedes memory exhaustion - so its panel
  // marks the level rather than leaving a climbing line looking ordinary.
  // Drawn as a labelled dashed rule AND a colour change, never colour alone.
  readonly threshold?: number | null;
}): JSX.Element => {
  const [hover, setHover] = useState<Hover | null>(null);
  const n = series.points.length;
  const usable = HEIGHT - PAD_TOP - PAD_BOTTOM;
  const plotW = WIDTH - AXIS_W;
  const stepX = n > 1 ? plotW / (n - 1) : plotW;
  const xOf = (i: number): number => AXIS_W + i * stepX;
  // Percent metrics are 0-100; clamp so an occasional >100 spike can't escape.
  const yOf = (value: number): number => {
    const clamped = Math.min(100, Math.max(0, value));
    return PAD_TOP + (1 - clamped / 100) * usable;
  };
  const pts: ReadonlyArray<Pt | null> = series.points.map((p, i) =>
    p.value == null ? null : { x: xOf(i), y: yOf(p.value), value: p.value, t: p.t, i },
  );
  const segments = toSegments(pts);
  const firstSeg = segments[0];
  const areaPath =
    firstSeg !== undefined && firstSeg.length > 1
      ? `M ${firstSeg[0]!.x} ${HEIGHT - PAD_BOTTOM} ` +
        firstSeg.map((p) => `L ${p.x} ${p.y}`).join(" ") +
        ` L ${firstSeg[firstSeg.length - 1]!.x} ${HEIGHT - PAD_BOTTOM} Z`
      : null;
  // A lone non-null point (both neighbours null) makes a 1-point segment that
  // draws no line, so mark it with a dot or it would be invisible.
  const dots = pts.flatMap((p, i) =>
    p !== null && pts[i - 1] == null && pts[i + 1] == null ? [p] : [],
  );
  const label = METRIC_LABEL[series.metric] ?? series.metric;
  // A hard-stop gradient at the threshold's y paints the part of the line above
  // it in the danger colour, so a breach is legible from the shape of the trace
  // rather than needing the reader to compare against the axis.
  const hasThreshold = threshold != null && threshold > 0 && threshold < 100;
  const gradId = `metric-threshold-${series.metric}`;
  const thresholdY = hasThreshold ? yOf(threshold) : 0;
  const stop = (thresholdY / HEIGHT).toFixed(4);
  // Applied as inline style, NOT a stroke/fill attribute: the .line/.area CSS
  // classes set stroke and fill, and a CSS rule beats an SVG presentation
  // attribute, so the gradient would silently never paint.
  const onEnter =
    (p: Pt) =>
    (e: React.MouseEvent<SVGRectElement>): void => {
      const r = e.currentTarget.getBoundingClientRect();
      setHover({ value: p.value, t: p.t, cx: r.left + r.width / 2, top: r.top, bottom: r.bottom });
    };

  return (
    <>
      <svg
        className={styles.chart}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={
          hasThreshold
            ? `${label} usage percent, danger threshold ${threshold}%`
            : `${label} usage percent`
        }
      >
        {hasThreshold && (
          <defs>
            {/* userSpaceOnUse, NOT objectBoundingBox: bounding-box offsets are
                relative to each element's own extent, so a polyline spanning
                only part of the chart height would put the colour change at the
                wrong value. Anchoring to the viewBox keeps the transition
                exactly on the threshold for every element that references it. */}
            <linearGradient id={gradId} gradientUnits="userSpaceOnUse" x1={0} y1={0} x2={0} y2={HEIGHT}>
              <stop offset="0" stopColor="var(--status-danger)" />
              <stop offset={stop} stopColor="var(--status-danger)" />
              <stop offset={stop} stopColor="#1971c2" />
              <stop offset="1" stopColor="#1971c2" />
            </linearGradient>
          </defs>
        )}
        {[0, 25, 50, 75, 100].map((g) => (
          <line key={g} className={styles.grid} x1={AXIS_W} x2={WIDTH} y1={yOf(g)} y2={yOf(g)} aria-hidden />
        ))}
        {hasThreshold && (
          <>
            <line
              className={styles.threshold}
              x1={AXIS_W}
              x2={WIDTH}
              y1={thresholdY}
              y2={thresholdY}
              aria-hidden
            />
            <text className={styles.thresholdLabel} x={WIDTH} y={thresholdY - 3} textAnchor="end" aria-hidden>
              {threshold}%
            </text>
          </>
        )}
        {[100, 50, 0].map((g) => (
          <text key={g} className={styles.axisLabel} x={0} y={yOf(g) + 3.2} aria-hidden>
            {g === 0 ? "0" : `${g}%`}
          </text>
        ))}
        {areaPath !== null && (
          <path
            className={styles.area}
            d={areaPath}
            {...(hasThreshold
              ? { style: { fill: `url(#${gradId})`, fillOpacity: 0.14 } }
              : {})}
          />
        )}
        {segments
          .filter((seg) => seg.length > 1)
          .map((seg, si) => (
            <polyline
              key={si}
              className={styles.line}
              points={seg.map((p) => `${p.x},${p.y}`).join(" ")}
              fill="none"
              {...(hasThreshold ? { style: { stroke: `url(#${gradId})` } } : {})}
            />
          ))}
        {dots.map((p) => (
          <circle key={p.i} className={styles.dot} cx={p.x} cy={p.y} r={2.25} />
        ))}
        {pts.map((p, i) =>
          p === null ? null : (
            <rect
              key={i}
              className={styles.hit}
              x={p.x - stepX / 2}
              y={0}
              width={stepX}
              height={HEIGHT}
              aria-label={`${bucketRangeLabel(p.t, bucketSeconds)}: ${metricValueLabel(p.value, totalGib)}`}
              onMouseEnter={onEnter(p)}
              onMouseLeave={() => setHover(null)}
            />
          ),
        )}
      </svg>
      {hover != null && (
        <ChartTooltip anchor={hover}>
          <div className={tip.tipHead}>
            <span className={tip.tipState}>{label}</span>
            <span className={tip.tipTime}>{bucketRangeLabel(hover.t, bucketSeconds)}</span>
          </div>
          <div className={tip.tipBody}>{metricValueLabel(hover.value, totalGib)}</div>
        </ChartTooltip>
      )}
    </>
  );
};
